import React, { Component } from 'react';

import qs from 'qs';
import {Alert, Button, Dialog, TextField} from '@betareader/design-system';
// i18n
import { withTranslation } from 'react-i18next';

// Routes
import { withRouter } from 'react-router-dom';

// redux
import { push } from '../../navigation';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';

import InviteReader from './inviteReader';
import ReaderIndexFilterContainer from './readerIndexFilterContainer';
import { authUtil, featureToggleUtil } from '../../utils';
import UserBadge from '../../components/user/userBadge';
import { fetchReaders, fetchMoreReaders } from '../../modules/readerIndex';
import LoadMore from '../../components/lists/loadMore';
import { pick } from '../../shared/utils/randomUtil';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

const sortOrder = 'recommended';
const legacySortOrder = pick([
  { value: 'joinDate', weight: 0.95 },
  { value: 'alphabetic', weight: 0.05 },
]).value;

export class ReaderIndex extends Component {
  constructor(props) {
    super(props);
    this.state = {
      nameFilter: '',
    };
  }

  componentDidMount = () => {
    this.fetchUsers();
  };

  componentWillUnmount() {
    clearTimeout(this.fetchUsersTimeout);
  }

  buildQuery = skip => {
    const { nameFilter } = this.state;
    const { readerFilters } = this.props;
    const genreFilters = readerFilters.filter(
      filters => filters.type === 'genres'
    );
    const languageFilters = readerFilters.filter(
      filters => filters.type === 'language'
    );
    const ratingFilters = readerFilters.filter(
      filters => filters.type === 'rating'
    );
    const genres = {};
    const languages = {};
    const rating = {};
    let query = {
      name: nameFilter,
      sortOrder,
      legacySortOrder,
      skip,
    };
    if (Array.isArray(genreFilters) && genreFilters.length > 0) {
      genreFilters.forEach(filter => {
        if (filter.option && filter.value) {
          genres[filter.option] = filter.value;
        }
      });
      query = {
        ...query,
        genres,
      };
    }
    if (Array.isArray(languageFilters) && languageFilters.length > 0) {
      languageFilters.forEach(filter => {
        if (filter.option && filter.value) {
          languages[filter.option] = filter.value;
        }
      });
      query = {
        ...query,
        languages,
      };
    }
    if (Array.isArray(ratingFilters) && ratingFilters.length > 0) {
      ratingFilters.forEach(filter => {
        if (filter.option && filter.value) {
          rating[filter.option] = filter.value;
        }
      });
      query = {
        ...query,
        rating,
      };
    }
    return qs.stringify(query);
  };

  fetchUsers = async () => {
    clearTimeout(this.fetchUsersTimeout);
    const idToken = await authUtil.getFreshIdToken();
    this.props.fetchReaders(idToken, this.buildQuery(0));
  };

  fetchMoreUsers = async () => {
    const idToken = await authUtil.getFreshIdToken();
    const { readers = [] } = this.props;
    this.props.fetchMoreReaders(
      idToken,
      this.buildQuery(readers.filter(reader => reader._id).length)
    );
  };

  handleUserFilterChange = event => {
    this.setState(
      {
        nameFilter: event.target.value,
      },
      () => {
        const self = this;
        clearTimeout(this.fetchUsersTimeout);
        this.fetchUsersTimeout = setTimeout(() => {
          self.fetchUsers();
        }, 500);
      }
    );
  };

  /* Checks if the displayName or username matches the nameFilter if any */
  matchesFilter = reader => {
    if (!reader._id) return false; // return false if this is not a real reader entry
    const { nameFilter } = this.state;
    if (!nameFilter || nameFilter.length === 0) return true;
    const search = nameFilter.toLocaleLowerCase();
    return (
      reader.displayName?.toLocaleLowerCase().includes(search) ||
      reader.username?.toLocaleLowerCase().includes(search)
    );
  };

  render() {
    const {
      t,
      readers = [],
      userProfile,
      changePage,
      aiEditorEnabled,
    } = this.props;
    const { nameFilter, showModal, loadingMoreUsers } = this.state;
    // Free plans browse the pool as a teaser: reader identities are blurred and
    // inviting prompts an upgrade (see UserBadge `locked`). Gated behind the
    // same AI Editor rollout flag so the teaser only appears where that
    // premium surface is live.
    const isFree =
      !userProfile?.account?.product ||
      userProfile.account.product.internalId === 'free';
    const showTeaser = isFree && aiEditorEnabled;
    return (
      <div style={{ padding: 10 }}>
        <div>{t('ReaderIndexInfo')}</div>
        <ReaderIndexFilterContainer onUpdate={this.fetchUsers} />
        <TextField
          id='reader-index-search'
          label={t('Search')}
          placeholder={t('Search')}
          value={nameFilter}
          onChange={this.handleUserFilterChange}
        />
        {showTeaser && (
          <Alert variant='info'>
              <h2>{t('ReaderPoolLockedHeader', 'Meet your beta readers')}</h2>
              {t(
                'ReaderPoolLockedBody',
                'These readers match your books. Upgrade to a paid plan to see who they are and invite them to your beta.'
              )}
              <div style={{ marginTop: 12 }}>
                <Button onClick={() => changePage('/products')}>
                  {t('UpgradeToUnlock', 'Upgrade to unlock')}
                </Button>
              </div>
          </Alert>
        )}
        <div>
          {readers
            .filter(reader => this.matchesFilter(reader))
            .map(reader => (
              <UserBadge
                key={reader._id}
                user={reader}
                locked={showTeaser}
                onInvite={() =>
                  showTeaser
                    ? changePage('/products')
                    : this.setState({
                        showModal: {
                          inviteReader: reader,
                        },
                      })
                }
              />
            ))}
        </div>
        {readers.length > 0 && !readers[readers.length - 1].isLast && (
          <LoadMore
            onLoadMore={this.fetchMoreUsers}
            loading={loadingMoreUsers}
          />
        )}
        <Dialog
          open={Boolean(showModal)}
          title={t('SelectBook')}
          onClose={() => this.setState({showModal: false})}
          actions={<Button variant='secondary' onClick={() => this.setState({showModal: false})}>{t('Close')}</Button>}>
          {showModal?.inviteReader && (
            <InviteReader
              reader={showModal.inviteReader}
              onClose={() => this.setState({showModal: false})}
              source={showModal.inviteReader.rankingVariant
                ? `readerindex_rank_${showModal.inviteReader.rankingVariant}`
                : `readerindex_sort_${legacySortOrder}`}
            />
          )}
        </Dialog>
      </div>
    );
  }
}

// redux stuff
const mapStateToProps = state => ({
  userProfile: state.user.userProfile,
  readers: state.readerIndex.readers,
  readerFilters: state.readerIndex.readerFilters,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      fetchReaders,
      fetchMoreReaders,
      changePage: newPage => push(newPage),
    },
    dispatch
  );

const ConnectedReaderIndex = withTranslation()(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(ReaderIndex))
);

// Bridge the AI Editor feature flag (a hook) into the connected class so the
// reader-pool teaser only shows where AI Editor is enabled — same gate as the
// AI Editor report surface (flags?.ai_editor || per-user betaFeatures.ai_editor).
const ReaderIndexWithFlags = props => {
  const { flags, isLoading: flagsLoading } = useFeatureFlags();
  const userProfile = useSelector(state => state.user.userProfile);
  const aiEditorEnabled =
    (!flagsLoading && flags?.ai_editor === true) ||
    featureToggleUtil.hasFeatureAccess(userProfile, 'ai_editor');
  return <ConnectedReaderIndex {...props} aiEditorEnabled={aiEditorEnabled} />;
};

export default ReaderIndexWithFlags;
